import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { QueueTable } from '../../components/sizes/QueueTable/QueueTable';
import { SizesTable } from '../../components/sizes/SizesTable/SizesTable';
import { SizesToolbar } from '../../components/sizes/SizesToolbar/SizesToolbar';
import { Button } from '../../components/ui/Button/Button';
import { AddSizeModal } from '../../components/sizes/AddSizeModal/AddSizeModal';
import type { NewSizeValues } from '../../components/sizes/AddSizeModal/AddSizeModal';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { Notice } from '../../components/ui/Notice/Notice';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { Tabs, type TabItem } from '../../components/ui/Tabs/Tabs';
import { useToast } from '../../components/ui/Toast/ToastProvider';

import {
    useAddToHandbookMutation,
    useCreateStandardSizeMutation,
    useDeleteStandardSizeMutation,
    useLazyGetStandardSizesQuery,
    useRemoveFromQueueMutation,
    useGetSizeQueueQuery,
    useGetStandardSizesQuery,
    useUpdateStandardSizeMutation,
    useUpdateQueueItemMutation,
} from '../../entities/handbook/api/sizeApi';
import { downloadQueue, downloadSizes } from '../../entities/handbook/api/exportSizes';
import { sortRows, useQueueFilter, useSizesTable } from '../../hooks/useSizes';
import type { QueueItem, TypicalSize } from '../../types/sizes';
import { formatRangeLabel } from '../../utils/format';
import { formatNumber } from '../../utils/number';
import { getErrorMessage, getErrorStatus } from '../../shared/api/errors';
import styles from './SizesPage.module.scss';

type SizesTab = 'sizes' | 'queue';

const TAB_PATH: Record<SizesTab, string> = {
    sizes: '/handbook/sizes/standard_size',
    queue: '/handbook/sizes/standard_size_report',
};
const QUEUE_PAGE_SIZE = 75;

const DUPLICATE_MESSAGE = 'Товар с таким наименованием уже есть в справочнике';

export function SizesPage() {
    const notify = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    const tab: SizesTab = location.pathname.endsWith('standard_size_report') ? 'queue' : 'sizes';

    const sizes = useSizesTable();
    const queue = useQueueFilter();

    const {
        data: sizesPage,
        isLoading: isSizesLoading,
        isFetching: isSizesFetching,
    } = useGetStandardSizesQuery({
        page_num: sizes.page,
        page_size: sizes.perPage,
        query: sizes.appliedQuery,
    });

    const {
        data: queuePage,
        isLoading: isQueueLoading,
        isFetching: isQueueFetching,
    } = useGetSizeQueueQuery({
        page_num: queue.page,
        page_size: QUEUE_PAGE_SIZE,
        query: queue.appliedQuery,
    });

    const rows = useMemo(() => sortRows(sizesPage?.items ?? [], sizes.sort), [sizesPage, sizes.sort]);
    const queueItems = queuePage?.items ?? [];

    const total = sizesPage?.total ?? 0;
    const queueTotal = queuePage?.total ?? 0;

    const tabs: TabItem<SizesTab>[] = [
        { key: 'sizes', label: 'Справочник габаритов (Эталон)', badge: { value: formatNumber(total) } },
        {
            key: 'queue',
            label: 'Неподтверждённые габариты',
            badge: { value: queueTotal, tone: 'warning' },
        },
    ];

    const [addToHandbook] = useAddToHandbookMutation();
    const [removeFromQueue] = useRemoveFromQueueMutation();
    const [findSizes] = useLazyGetStandardSizesQuery();
    const [updateSize] = useUpdateStandardSizeMutation();
    const [deleteSize, { isLoading: isDeleting }] = useDeleteStandardSizeMutation();
    const [createSize, { isLoading: isCreating }] = useCreateStandardSizeMutation();
    const [updateQueueItem] = useUpdateQueueItemMutation();

    const [isExportingSizes, setIsExportingSizes] = useState(false);
    const [isExportingQueue, setIsExportingQueue] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState<TypicalSize | null>(null);
    const [isAdding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    const createFromForm = async (values: NewSizeValues) => {
        setAddError(null);

        try {
            const created = await createSize(values).unwrap();

            notify(`«${created.name}» добавлен в справочник`);
            setAdding(false);
        } catch (error) {
            /* 409 бэкенд объясняет обобщённо — подставляем понятную причину. */
            setAddError(
                getErrorStatus(error) === 409
                    ? DUPLICATE_MESSAGE
                    : getErrorMessage(error, 'Не удалось добавить товар'),
            );
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        try {
            await deleteSize(deleteTarget.id).unwrap();
        } catch (error) {
            notify(getErrorMessage(error, 'Не удалось удалить запись'));
            return;
        }

        notify('Запись удалена из справочника');
        setDeleteTarget(null);
    };

    const saveSize = async (id: string, values: Record<string, unknown>) => {
        try {
            await updateSize({ id, body: values }).unwrap();
        } catch (error) {
            notify(getErrorMessage(error, 'Не удалось сохранить габариты'));
            return;
        }

        notify('Габариты сохранены');
    };

    const addFromQueue = async (item: QueueItem, values?: Record<string, number | null>) => {
        if (values) {
            try {
                await updateQueueItem({ id: item.id, body: values }).unwrap();
            } catch (error) {
                notify(getErrorMessage(error, 'Не удалось сохранить габариты'));
                return;
            }
        }

        try {
            await addToHandbook([item.id]).unwrap();
        } catch (error) {
            notify(
                getErrorStatus(error) === 409
                    ? DUPLICATE_MESSAGE
                    : getErrorMessage(error, 'Не удалось перенести позицию'),
            );
            return;
        }

        notify(values ? 'Позиция перенесена с исправленными габаритами' : 'Позиция перенесена в справочник');
    };

    const deleteFromQueue = async (item: QueueItem) => {
        try {
            await removeFromQueue([item.id]).unwrap();
        } catch (error) {
            notify(getErrorMessage(error, 'Не удалось убрать позицию из очереди'));
            return;
        }

        notify('Позиция убрана из очереди');
    };

    const exportQueue = async () => {
        setIsExportingQueue(true);

        try {
            await downloadQueue();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Не удалось выгрузить список');
        } finally {
            setIsExportingQueue(false);
        }
    };

    const exportSizes = async () => {
        setIsExportingSizes(true);

        try {
            await downloadSizes();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Не удалось выгрузить справочник');
        } finally {
            setIsExportingSizes(false);
        }
    };

    return (
        <div className={styles.page}>
            <PageHeader
                title="Габариты"
                className={styles.header}
                actions={
                    <Button variant="primary" onClick={() => setAdding(true)}>
                        Добавить новый товар
                    </Button>
                }
            />

            <Tabs items={tabs} active={tab} onChange={(next) => navigate(TAB_PATH[next])} />

            {tab === 'sizes' ? (
                <section className={styles.pane} role="tabpanel" aria-label="Справочник габаритов">
                    <SizesToolbar
                        query={sizes.query}
                        placeholder="Наименование товара"
                        onQueryChange={sizes.setQuery}
                        onExport={exportSizes}
                        busy={isExportingSizes}
                    />

                    <SizesTable
                        sizes={rows}
                        startIndex={(sizes.page - 1) * sizes.perPage}
                        sort={sizes.sort}
                        scrollKey={`${sizes.page}-${sizes.perPage}-${sizes.appliedQuery}-${JSON.stringify(sizes.sort)}`}
                        isLoading={isSizesLoading}
                        loading={isSizesFetching}
                        loadingText="Загружаем справочник…"
                        onSort={sizes.toggleSort}
                        onSave={saveSize}
                        onDelete={setDeleteTarget}
                        footer={
                            total > 0 ? (
                                <TableFooter
                                    page={sizes.page}
                                    totalPages={sizesPage?.pages ?? 1}
                                    rangeLabel={formatRangeLabel(sizes.page, sizes.perPage, total)}
                                    onPageChange={sizes.setPage}
                                />
                            ) : undefined
                        }
                        empty={
                            rows.length === 0 ? (
                                <EmptyState
                                    title="Ничего не нашлось"
                                    description="Проверьте написание или очистите поиск."
                                    action={sizes.isFiltered ? { label: 'Очистить поиск', onClick: sizes.reset } : undefined}
                                />
                            ) : undefined
                        }
                    />
                </section>
            ) : (
                <section className={styles.pane} role="tabpanel" aria-label="Неподтверждённые габариты">
                    <Notice>
                        Товары из проектов, чьи габариты <b>нашлись на сайте продавца</b>, но ещё не
                        внесены в справочник. Добавьте их — и в следующих проектах размеры подставятся
                        сами, без повторного поиска.
                    </Notice>

                    <SizesToolbar
                        query={queue.query}
                        placeholder="Наименование или проект"
                        onQueryChange={queue.setQuery}
                        onExport={exportQueue}
                        busy={isExportingQueue}
                    />

                    <QueueTable
                        items={queueItems}
                        startIndex={(queue.page - 1) * QUEUE_PAGE_SIZE}
                        scrollKey={`${queue.page}-${queue.appliedQuery}`}
                        isLoading={isQueueLoading}
                        loading={isQueueFetching}
                        loadingText="Загружаем очередь…"
                        onAdd={addFromQueue}
                        onDelete={deleteFromQueue}
                        footer={
                            queueTotal > 0 ? (
                                <TableFooter
                                    page={queue.page}
                                    totalPages={queuePage?.pages ?? 1}
                                    rangeLabel={formatRangeLabel(queue.page, QUEUE_PAGE_SIZE, queueTotal)}
                                    onPageChange={queue.setPage}
                                />
                            ) : undefined
                        }
                        empty={
                            queueItems.length === 0 ? (
                                <EmptyState
                                    title="Очередь пуста"
                                    description="Все найденные габариты уже внесены в справочник."
                                />
                            ) : undefined
                        }
                    />
                </section>
            )}

            <AddSizeModal
                open={isAdding}
                busy={isCreating}
                error={addError}
                onSubmit={createFromForm}
                onClose={() => {
                    setAdding(false);
                    setAddError(null);
                }}
            />

            <ConfirmModal
                open={Boolean(deleteTarget)}
                danger
                busy={isDeleting}
                title="Удалить типовой размер?"
                description={
                    <>
                        Запись исчезнет из справочника.
                    </>
                }
                confirmLabel="Удалить"
                onConfirm={confirmDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}